import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * How long a shade change waits for the incoming image before swapping anyway.
 * Long enough to cover a slow connection, short enough that a failed image can
 * never strand the gallery on the previous shade.
 */
const SHADE_SWAP_TIMEOUT = 2000;

/**
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent]
 * @property {import('./slideshow').Slideshow} [slideshow]
 * @property {HTMLElement[]} [media]
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();
  #slideshowListenersBound = false;
  #slideshowThumbnailSyncPatched = false;
  #setupGeneration = 0;
  /** @type {MutationObserver | null} */
  #klaviyoFavoritesObserver = null;
  /** @type {number | null} */
  #klaviyoFavoritesFrame = null;
  /** @type {ResizeObserver | null} */
  #thumbnailResizeObserver = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #thumbnailFixTimer;
  /** Guards against an earlier shade change landing after a later one. */
  #shadeSwapToken = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.addEventListener('click', this.#handleDotClick, { capture: true, signal });
    this.addEventListener('click', this.#handleCustomVideoClick, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#fixPdpThumbnailControls();
    this.#scheduleGallerySetup();
    this.#initCustomVideoGridAutoplay();
    this.#resolveVimeoPosters();
    this.#setupKlaviyoFavoritesPlacement();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#setupGeneration += 1;
    this.#klaviyoFavoritesObserver?.disconnect();
    if (this.#klaviyoFavoritesFrame !== null) {
      cancelAnimationFrame(this.#klaviyoFavoritesFrame);
      this.#klaviyoFavoritesFrame = null;
    }
    this.#thumbnailResizeObserver?.disconnect();
    this.#thumbnailResizeObserver = null;
    window.clearTimeout(this.#thumbnailFixTimer);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;
    if (!source) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;

    // Pending keys bypass the `data-variant-media-filter` flag, so skip them when
    // the incoming markup was already filtered down to one shade by Liquid.
    const serverFiltered = newMediaGallery.getAttribute('data-server-shade-filter') === 'true';
    const shadeKeys = serverFiltered ? [] : this.#getShadeKeysFromVariant(event.detail.resource);
    if (shadeKeys.length) {
      newMediaGallery.setAttribute('data-pending-shade-keys', shadeKeys.join('\u001e'));
    }

    if (serverFiltered) {
      this.#replaceWhenPrimaryImageReady(newMediaGallery);
      return;
    }

    this.replaceWith(newMediaGallery);
  };

  /**
   * Swap in a server-filtered gallery only once its first image is in the cache.
   *
   * Without filtering every shade's media was already in the document, so a
   * variant change swapped in the same image URLs and the browser had nothing to
   * fetch. Filtering means each shade brings its own URLs, and replacing the
   * element before they arrive paints an empty slide and remeasures the thumbnail
   * strip — the flicker. Decoding one image off-document first costs nothing
   * visible and lets the outgoing shade stay on screen until the new one can
   * paint in a single frame.
   *
   * @param {Element} newMediaGallery
   */
  #replaceWhenPrimaryImageReady(newMediaGallery) {
    const token = ++this.#shadeSwapToken;
    const image = newMediaGallery.querySelector('.product-media__image');

    const swap = () => {
      // A later shade change already superseded this one, or we were removed.
      if (token !== this.#shadeSwapToken || !this.isConnected) return;
      this.replaceWith(newMediaGallery);
    };

    if (!(image instanceof HTMLImageElement)) {
      swap();
      return;
    }

    // Mirror srcset and sizes so the preload resolves to the same candidate the
    // real <img> will pick; loading a different width would warm the wrong entry.
    const preload = new Image();
    if (image.sizes) preload.sizes = image.sizes;
    if (image.srcset) preload.srcset = image.srcset;
    preload.src = image.src;

    if (preload.complete) {
      swap();
      return;
    }

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      swap();
    };
    const timer = setTimeout(settle, SHADE_SWAP_TIMEOUT);

    preload.addEventListener('load', settle, { once: true });
    preload.addEventListener('error', settle, { once: true });
  }

  /**
   * Declarative thumbnail handler (`on:click="media-gallery/selectThumbnail"`).
   * Must live on media-gallery — the theme's document click router runs before
   * element capture handlers, so `/select/N` on thumbnails wins the race and
   * uses stale absolute indices after shade filtering / video reordering.
   * @param {Event} event
   */
  selectThumbnail(event) {
    const button =
      event.target instanceof Element ? event.target.closest('.slideshow-controls__thumbnail') : null;
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;
    if (button.hasAttribute('hidden') || button.getAttribute('aria-hidden') === 'true') return;

    const thumbnailsContainer = button.closest('.slideshow-controls__thumbnails-container');
    if (thumbnailsContainer?.hasAttribute('data-dragged')) {
      thumbnailsContainer.removeAttribute('data-dragged');
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const slideId = button.getAttribute('data-slide-id');
    if (!slideId) return;

    event.preventDefault();
    event.stopPropagation();

    // Freeze strip scroll: mobile browsers scroll focused buttons into view,
    // which makes the thumbnails list look like it is swiping on video tap.
    const scrollLocks = this.#captureThumbnailScrollPositions();
    const restoreScroll = () => this.#restoreThumbnailScrollPositions(scrollLocks);

    try {
      button.focus({ preventScroll: true });
    } catch {
      // ignore
    }

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const slides = this.#getSlides();
    const targetSlide = slides.find(
      (slide) => slide instanceof HTMLElement && slide.getAttribute('slide-id') == slideId
    );

    this.#pauseCustomVideos();
    slideshow.select({ id: slideId }, event, { animate: false });
    restoreScroll();

    if (!(targetSlide instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      restoreScroll();
      slides.forEach((slide) => {
        if (!(slide instanceof HTMLElement)) return;
        if (slide.hasAttribute('hidden') && !slide.hasAttribute('reveal')) return;
        slide.setAttribute('aria-hidden', slide === targetSlide ? 'false' : 'true');
      });
      this.#syncThumbnailSelection(slides, targetSlide);
      this.#syncActiveCustomVideo();
      restoreScroll();
      requestAnimationFrame(restoreScroll);
    });
  }

  /**
   * @returns {{ el: HTMLElement, left: number, top: number }[]}
   */
  #captureThumbnailScrollPositions() {
    return Array.from(this.querySelectorAll('.slideshow-controls__thumbnails-container')).flatMap((el) => {
      if (!(el instanceof HTMLElement)) return [];
      return [{ el, left: el.scrollLeft, top: el.scrollTop }];
    });
  }

  /**
   * @param {{ el: HTMLElement, left: number, top: number }[]} locks
   */
  #restoreThumbnailScrollPositions(locks) {
    for (const { el, left, top } of locks) {
      if (!el.isConnected) continue;
      el.scrollLeft = left;
      el.scrollTop = top;
    }
  }

  /**
   * @param {Event} event
   */
  #handleDotClick = (event) => {
    if (this.dataset.variantMediaFilter !== 'true') return;

    const button = event.target instanceof Element ? event.target.closest('.slideshow-controls__dots button') : null;
    if (!(button instanceof HTMLElement) || !this.contains(button)) return;

    const list = button.closest('.slideshow-controls__dots');
    const li = button.closest('li');
    if (!(list instanceof HTMLElement) || !(li instanceof HTMLElement) || li.hasAttribute('hidden')) return;

    const slideIndex = Array.from(list.querySelectorAll('li')).indexOf(li);
    const slides = this.#getSlides();
    const slide = slides[slideIndex];

    if (!slide || slide.hasAttribute('hidden')) return;

    const slideId = slide.getAttribute('slide-id');
    if (!slideId) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.slideshow?.select({ id: slideId }, undefined, { animate: false });
  };

  /**
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const slide = event.detail.slide;
    const customSlide =
      slide instanceof HTMLElement ? slide.closest('[data-custom-metafield-video]') : null;

    this.#pauseCustomVideos();

    if (customSlide instanceof HTMLElement) {
      this.#playCustomVideo(customSlide);
    }

    if (slide instanceof HTMLElement) {
      requestAnimationFrame(() => {
        this.#syncThumbnailSelection(this.#getSlides(), slide);
      });
    }
  };

  /**
   * @param {ZoomMediaSelectedEvent} event
   */
  #handleZoomMediaSelected = async (event) => {
    const zoomMedia = this.refs.zoomDialogComponent?.refs?.media?.[event.detail.index];
    const mediaId = zoomMedia instanceof HTMLElement ? zoomMedia.getAttribute('data-media-id') : null;

    if (mediaId) {
      this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
      return;
    }

    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  #initCustomVideoGridAutoplay() {
    if (this.dataset.presentation !== 'grid') return;

    const items = this.querySelectorAll('[data-custom-metafield-video]');
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;
          if (!(slide instanceof HTMLElement)) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            this.#playCustomVideo(slide);
          } else {
            this.#stopCustomVideo(slide);
          }
        });
      },
      { threshold: [0.5] }
    );

    items.forEach((item) => observer.observe(item));
  }

  #scheduleGallerySetup() {
    const generation = ++this.#setupGeneration;

    const run = () => {
      if (!this.isConnected || generation !== this.#setupGeneration) return;

      this.#bindSlideshowListeners();
      this.#patchSlideshowThumbnailSync();

      const applied = this.#applyGalleryState();
      if (!applied) return;

      this.#syncActiveCustomVideo();
    };

    run();
    requestAnimationFrame(run);
    window.setTimeout(run, 100);
    window.setTimeout(run, 250);
    window.setTimeout(run, 500);
  }

  /**
   * @returns {boolean} Whether gallery state was applied
   */
  #applyGalleryState() {
    if (this.dataset.variantMediaFilter !== 'true') return true;

    this.#pauseCustomVideos();

    const slideshow = this.slideshow;
    if (!slideshow) return false;

    let slides = this.#getSlides();
    if (!slides.length) return false;

    const mediaKeys = this.#getVariantMediaKeys();
    if (!mediaKeys.length) return false;

    const customVideoSlide = slides.find(
      (slide) => slide instanceof HTMLElement && slide.hasAttribute('data-custom-metafield-video')
    );

    /** @type {HTMLElement[]} */
    const matchingImages = [];

    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;

      if (this.#isSwatchMediaSlide(slide)) {
        this.#setSlideHidden(slide, true);
        continue;
      }

      if (this.#isCustomMetafieldVideoSlide(slide)) {
        continue;
      }

      if (this.#slideMatchesMediaKeys(slide, mediaKeys)) {
        matchingImages.push(slide);
      } else {
        this.#setSlideHidden(slide, true);
      }
    }

    if (!matchingImages.length) {
      const fallback =
        slides.find(
          (slide) =>
            slide instanceof HTMLElement &&
            !this.#isSwatchMediaSlide(slide) &&
            !this.#isCustomMetafieldVideoSlide(slide)
        ) ?? null;

      if (fallback instanceof HTMLElement) {
        matchingImages.push(fallback);
        this.#setSlideHidden(fallback, false);
      }
    }

    /** @type {HTMLElement[]} */
    // Keep all matching images first; append custom video last so
    // thumbnail click indexes stay aligned with slide indexes.
    const visibleOrder = [...matchingImages];

    if (customVideoSlide instanceof HTMLElement) {
      this.#setSlideHidden(customVideoSlide, false);
      visibleOrder.push(customVideoSlide);
    }

    const activeSlide =
      matchingImages[0] ??
      visibleOrder.find((slide) => !this.#isCustomMetafieldVideoSlide(slide)) ??
      visibleOrder[0];

    for (const slide of slides) {
      if (!(slide instanceof HTMLElement)) continue;
      slide.setAttribute('aria-hidden', slide === activeSlide ? 'false' : 'true');
    }

    this.#reorderSlidesInDom(slides, visibleOrder);
    slides = this.#getSlides();

    this.#syncThumbnailOrderFromSlideIds(slides, visibleOrder);
    this.#syncLinkedMediaVisibility(slides);
    this.#syncZoomDialogFromVisibleOrder(visibleOrder);

    const slideId = activeSlide?.getAttribute('slide-id');
    requestAnimationFrame(() => {
      if (slideId) {
        slideshow.select({ id: slideId }, undefined, { animate: false });
      } else if (activeSlide) {
        const index = slides.indexOf(activeSlide);
        if (index >= 0) {
          slideshow.select(index, undefined, { animate: false });
        }
      }

      if (activeSlide) {
        this.#syncThumbnailSelection(slides, activeSlide);
      }
      this.#syncActiveCustomVideo();
    });

    return true;
  }

  #bindSlideshowListeners() {
    if (this.#slideshowListenersBound) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    this.#slideshowListenersBound = true;

    const { signal } = this.#controller;
    slideshow.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    const scroller = slideshow.refs?.scroller;
    if (scroller instanceof HTMLElement) {
      scroller.addEventListener(
        'scroll',
        () => {
          requestAnimationFrame(() => {
            const active = slideshow.slides?.[slideshow.current ?? 0];
            if (active instanceof HTMLElement) {
              this.#syncThumbnailSelection(this.#getSlides(), active);
            }
          });
        },
        { signal, passive: true }
      );
    }
  }

  #patchSlideshowThumbnailSync() {
    if (this.#slideshowThumbnailSyncPatched) return;

    const slideshow = this.slideshow;
    if (!slideshow) return;

    const proto = Object.getPrototypeOf(slideshow);
    const descriptor = Object.getOwnPropertyDescriptor(proto, 'current');
    if (!descriptor?.set || !descriptor?.get) return;

    this.#slideshowThumbnailSyncPatched = true;

    const gallery = this;
    const originalSet = descriptor.set;

    Object.defineProperty(slideshow, 'current', {
      configurable: true,
      enumerable: true,
      get: descriptor.get,
      set(value) {
        originalSet.call(this, value);
        const activeSlide = this.slides?.[value];
        if (activeSlide instanceof HTMLElement) {
          gallery.#syncThumbnailSelection(gallery.#getSlides(), activeSlide);
        }
      },
    });
  }

  /**
   * Replace custom-video thumbnail fallbacks with the official Vimeo cover
   * from oEmbed (custom.video_link metafield).
   */
  #resolveVimeoPosters() {
    const images = this.querySelectorAll('img[data-vimeo-oembed-url]');
    if (!images.length) return;

    /** @type {Map<string, Promise<string | null>>} */
    const requests = new Map();

    /**
     * @param {string} vimeoUrl
     * @returns {Promise<string | null>}
     */
    const fetchThumbnail = (vimeoUrl) => {
      const cached = requests.get(vimeoUrl);
      if (cached) return cached;

      const request = fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(vimeoUrl)}`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => {
          const thumb = typeof data?.thumbnail_url === 'string' ? data.thumbnail_url : null;
          if (!thumb) return null;
          // Prefer a larger cover when Vimeo returns the small default size.
          return thumb.replace(/_\d+x\d+/, '_640x360');
        })
        .catch(() => null);

      requests.set(vimeoUrl, request);
      return request;
    };

    images.forEach((img) => {
      if (!(img instanceof HTMLImageElement)) return;
      const vimeoUrl = img.getAttribute('data-vimeo-oembed-url');
      if (!vimeoUrl) return;

      fetchThumbnail(vimeoUrl).then((thumbnailUrl) => {
        if (!thumbnailUrl || !img.isConnected) return;
        img.src = thumbnailUrl;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.removeAttribute('data-vimeo-oembed-url');
      });
    });
  }

  /**
   * Give a custom video its source the first time it is asked to play.
   *
   * The markup ships without `src` so page load costs nothing; see the comment in
   * `product-custom-video-link-media.liquid`. Setting `src` here starts the fetch
   * and the following `play()` picks it up once enough has buffered.
   *
   * @param {HTMLElement} media
   * @param {HTMLVideoElement} video
   */
  #attachCustomVideoSource(media, video) {
    if (video.getAttribute('src')) return;

    const source = media.dataset.videoSrc;
    if (!source) return;

    video.src = source;
  }

  #playCustomVideo(slide) {
    const media = slide.querySelector('.product-media--custom-video');
    if (!(media instanceof HTMLElement)) return;

    const video = media.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    this.#attachCustomVideoSource(media, video);

    video.play().catch(() => {});
    media.classList.add('is-playing');
  }

  #stopCustomVideo(slide) {
    const media = slide.querySelector('.product-media--custom-video');
    if (!(media instanceof HTMLElement)) return;

    const video = media.querySelector('video');
    video?.pause();
    // Rewinding a video that has no source yet would force it to load.
    if (video instanceof HTMLVideoElement && video.getAttribute('src')) {
      video.currentTime = 0;
    }
    media.classList.remove('is-playing');
  }

  /**
   * The zoom dialog renders its own copy of the custom video and nothing drives
   * it, so with `autoplay` gone it would sit on its poster forever. Play/pause on
   * click keeps it usable there without loading anything up front. Scoped to the
   * dialog so it cannot interfere with the carousel slide, where a click already
   * means zoom.
   *
   * @param {Event} event
   */
  #handleCustomVideoClick = (event) => {
    const target = event.target;
    const media = target instanceof Element ? target.closest('.product-media--custom-video') : null;
    if (!(media instanceof HTMLElement) || !media.closest('zoom-dialog')) return;

    const video = media.querySelector('video');
    if (!(video instanceof HTMLVideoElement)) return;

    this.#attachCustomVideoSource(media, video);

    if (video.paused) {
      video.play().catch(() => {});
      media.classList.add('is-playing');
    } else {
      video.pause();
      media.classList.remove('is-playing');
    }
  };

  #pauseCustomVideos() {
    this.querySelectorAll('[data-custom-metafield-video]').forEach((slide) => {
      if (slide instanceof HTMLElement) {
        this.#stopCustomVideo(slide);
      }
    });
  }

  #syncActiveCustomVideo() {
    const slides = this.#getSlides();
    const activeSlide = slides.find(
      (slide) => slide instanceof HTMLElement && slide.getAttribute('aria-hidden') === 'false'
    );

    slides.forEach((slide) => {
      if (!(slide instanceof HTMLElement) || !slide.hasAttribute('data-custom-metafield-video')) return;
      if (slide === activeSlide) {
        this.#playCustomVideo(slide);
      } else {
        this.#stopCustomVideo(slide);
      }
    });
  }

  /**
   * @param {object | null | undefined} variant
   * @returns {string[]}
   */
  #getShadeKeysFromVariant(variant) {
    if (!variant || typeof variant !== 'object') return [];

    /** @type {Set<string>} */
    const keys = new Set();

    const mediaTexts = [
      variant.featured_media?.preview_image?.src,
      variant.featured_media?.src,
      variant.featured_media?.alt,
      variant.featured_image?.src,
      variant.featured_image?.alt,
      variant.image?.src,
      variant.image?.alt,
    ];

    for (const text of mediaTexts) {
      this.#extractShadeKeysFromMediaText(text).forEach((key) => keys.add(key));
    }

    for (const value of [variant.option1, variant.option2, variant.option3, variant.title]) {
      if (typeof value !== 'string' || !value.trim()) continue;
      this.#normalizeShadeMediaKeys(value).forEach((key) => keys.add(key));
    }

    return [...keys];
  }

  /**
   * @returns {string[]}
   */
  #getVariantMediaKeys() {
    const pending = this.dataset.pendingShadeKeys;
    if (pending) {
      this.removeAttribute('data-pending-shade-keys');
      return pending.split('\u001e').filter(Boolean);
    }

    if (this.dataset.variantMediaFilter !== 'true') return [];

    const section = this.closest('.shopify-section, dialog, featured-product-information');
    const variantPicker = section?.querySelector('variant-picker');
    if (!variantPicker) return [];

    /** @type {HTMLInputElement[]} */
    const swatchInputs = Array.from(
      variantPicker.querySelectorAll('fieldset.variant-option--swatches input[type="radio"]:checked')
    ).filter((input) => input instanceof HTMLInputElement);

    const shadeInput =
      swatchInputs.find((input) => this.#isShadeOptionInput(input)) || swatchInputs[0];

    if (shadeInput instanceof HTMLInputElement) {
      const keys = this.#collectShadeMediaKeysFromInput(shadeInput);
      if (keys.length) return keys;
    }

    /** @type {Set<string>} */
    const collected = new Set();

    variantPicker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      this.#collectShadeMediaKeysFromInput(input).forEach((key) => collected.add(key));
    });

    const selectedSelect = variantPicker.querySelector('select');
    if (selectedSelect instanceof HTMLSelectElement) {
      const selectedOption = selectedSelect.selectedOptions?.[0];
      if (selectedOption) {
        const mediaKey = selectedOption.dataset?.shadeMediaKey?.trim();
        if (mediaKey) this.#normalizeShadeMediaKeys(mediaKey).forEach((key) => collected.add(key));
      }
      if (selectedSelect.value) {
        this.#normalizeShadeMediaKeys(selectedSelect.value).forEach((key) => collected.add(key));
      }
    }

    variantPicker.querySelectorAll('.variant-option__swatch-value').forEach((element) => {
      const text = element.textContent?.trim();
      if (text) this.#normalizeShadeMediaKeys(text).forEach((key) => collected.add(key));
    });

    return this.#sanitizeShadeMediaKeys([...collected]);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {string[]}
   */
  #collectShadeMediaKeysFromInput(input) {
    /** @type {Set<string>} */
    const keys = new Set();
    const mediaKey = input.dataset.shadeMediaKey?.trim();
    if (mediaKey) this.#normalizeShadeMediaKeys(mediaKey).forEach((key) => keys.add(key));
    if (input.value) this.#normalizeShadeMediaKeys(input.value).forEach((key) => keys.add(key));
    return this.#sanitizeShadeMediaKeys([...keys]);
  }

  /**
   * Drop keys that are too ambiguous (e.g. "1" from `_other_1` matching `/files/1/`).
   * @param {string[]} keys
   * @returns {string[]}
   */
  #sanitizeShadeMediaKeys(keys) {
    return keys.filter((key) => {
      if (!key) return false;
      const value = key.toLowerCase();
      if (/^\d+$/.test(value) && value.length <= 2) return false;
      return value.length >= 2;
    });
  }

  /**
   * Extract English shade keys embedded in media filenames/alts:
   * `_swatch_fair_cool` / `_other_010_1` → fair / 010, etc.
   * @param {string | null | undefined} text
   * @returns {string[]}
   */
  #extractShadeKeysFromMediaText(text) {
    if (!text || typeof text !== 'string') return [];

    const filename = text.split('/').pop()?.split('?')[0] || text;
    const normalized = filename.toLowerCase().replace(/\s+/g, '_');
    /** @type {Set<string>} */
    const keys = new Set();

    for (const marker of ['_swatch_', '_other_']) {
      const index = normalized.indexOf(marker);
      if (index === -1) continue;

      const after = normalized
        .slice(index + marker.length)
        .split(/[.?#&\s]/)[0]
        ?.replace(/[^a-z0-9_-]/g, '');

      if (!after) continue;

      const parts = after.split('_').filter(Boolean);
      const first = parts[0];
      if (!first) continue;

      // Skip sequence indexes (`_other_1`) — prefer shade code before the marker.
      if (/^\d{1,2}$/.test(first)) {
        const before = normalized.slice(0, index);
        const beforeParts = before.split('_').filter(Boolean);
        for (let i = beforeParts.length - 1; i >= 0; i -= 1) {
          const part = beforeParts[i];
          if (/^\d{3,}$/.test(part) || (/^[a-z]/.test(part) && part.length >= 3)) {
            keys.add(part);
            break;
          }
        }
        continue;
      }

      keys.add(after);
      keys.add(first);
    }

    return this.#sanitizeShadeMediaKeys([...keys]);
  }

  /**
   * @param {string} value
   * @returns {string[]}
   */
  #normalizeShadeMediaKeys(value) {
    if (!value) return [];

    /** @type {Set<string>} */
    const keys = new Set();

    /** @param {string | undefined} key */
    const addKey = (key) => {
      if (!key) return;
      keys.add(key.toLowerCase());
    };

    const normalized = value.toLowerCase().trim();
    addKey(normalized.replace(/\s+/g, '_'));
    addKey(normalized.replace(/\s+/g, '-'));

    const beforeDash = normalized.split(/[-–—]/)[0]?.trim();
    if (beforeDash) {
      addKey(beforeDash.replace(/\s+/g, '_'));
      const beforeDashParts = beforeDash.split(/\s+/).filter(Boolean);
      if (beforeDashParts.length) {
        addKey(beforeDashParts[0].replace(/\s+/g, '_'));
        addKey(beforeDashParts[beforeDashParts.length - 1].replace(/\s+/g, '_'));
      }
    }

    const parts = normalized.split(/\s+/).filter(Boolean);
    if (parts.length) {
      addKey(parts[parts.length - 1].replace(/\s+/g, '_'));
    }

    return this.#sanitizeShadeMediaKeys([...keys]);
  }

  /**
   * @param {HTMLInputElement} input
   * @returns {boolean}
   */
  #isShadeOptionInput(input) {
    if (input.dataset.shadeMediaKey) return true;
    if (input.closest('fieldset.variant-option--swatches')) return true;

    const name = input.name.toLowerCase();
    return (
      name.includes('shade') ||
      name.includes('color') ||
      name.includes('colour') ||
      name.includes('لون') ||
      name.includes('درجة') ||
      name.includes('ظل')
    );
  }

  /**
   * @param {HTMLElement} slide
   * @returns {boolean}
   */
  #isCustomMetafieldVideoSlide(slide) {
    return slide.hasAttribute('data-custom-metafield-video');
  }

  /**
   * @param {HTMLElement} slide
   * @returns {boolean}
   */
  #isSwatchMediaSlide(slide) {
    return this.#getSlideSearchText(slide).includes('swatch');
  }

  /**
   * Filename + alt only (never full CDN paths — `/files/1/` falsely matches key "1").
   * @param {HTMLElement} slide
   * @returns {string}
   */
  #getSlideSearchText(slide) {
    const img = slide.querySelector('img');
    const src = img?.getAttribute('src') || img?.currentSrc || '';
    const filename = src.split('/').pop()?.split('?')[0] || '';
    const parts = [
      slide.getAttribute('data-variant-media-search'),
      img?.getAttribute('alt'),
      filename,
    ];

    return parts.filter(Boolean).join(' ').toLowerCase();
  }

  /**
   * @param {HTMLElement} slide
   * @param {string[]} mediaKeys
   * @returns {boolean}
   */
  #slideMatchesMediaKeys(slide, mediaKeys) {
    const searchText = this.#getSlideSearchText(slide);
    if (!searchText || !mediaKeys.length) return false;

    return mediaKeys.some((key) => this.#haystackMatchesShadeKey(searchText, key));
  }

  /**
   * Prefer exact shade tokens (`_other_010`, `_010_`) so `010` never matches `005`.
   * @param {string} haystack
   * @param {string} key
   * @returns {boolean}
   */
  #haystackMatchesShadeKey(haystack, key) {
    if (!haystack || !key) return false;

    const k = key.toLowerCase();
    const escaped = k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    if (/^\d+$/.test(k)) {
      return (
        haystack.includes(`_other_${k}`) ||
        haystack.includes(`_swatch_${k}`) ||
        haystack.includes(`_${k}_`) ||
        haystack.includes(`_${k}.`) ||
        haystack.endsWith(`_${k}`) ||
        new RegExp(`(?:^|[^0-9])${escaped}(?:[^0-9]|$)`).test(haystack)
      );
    }

    return (
      haystack.includes(`_other_${k}`) ||
      haystack.includes(`_swatch_${k}`) ||
      haystack.includes(`_${k}_`) ||
      haystack.includes(k)
    );
  }

  /**
   * @returns {HTMLElement[]}
   */
  #getSlides() {
    const fromRefs = this.slideshow?.refs?.slides;
    if (fromRefs?.length) return fromRefs;

    return Array.from(this.querySelectorAll('slideshow-slide'));
  }

  /**
   * @param {HTMLElement} slide
   * @param {boolean} isHidden
   */
  #setSlideHidden(slide, isHidden) {
    if (isHidden) {
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
      slide.style.display = 'none';
    } else {
      slide.removeAttribute('hidden');
      slide.removeAttribute('reveal');
      slide.style.removeProperty('display');
    }
  }

  /**
   * @param {HTMLElement[]} slidesAll
   * @param {HTMLElement[]} visibleOrder
   */
  #reorderSlidesInDom(slidesAll, visibleOrder) {
    const scroller = this.slideshow?.refs?.scroller;
    if (!(scroller instanceof HTMLElement) || !visibleOrder.length) return;

    /** @type {HTMLElement[]} */
    const finalOrder = [...visibleOrder];

    for (const slide of slidesAll) {
      if (slide instanceof HTMLElement && !finalOrder.includes(slide)) {
        finalOrder.push(slide);
      }
    }

    for (const slide of finalOrder) {
      if (slide instanceof HTMLElement && scroller.contains(slide)) {
        scroller.appendChild(slide);
      }
    }
  }

  /**
   * Reorders and shows only thumbnails for visible slides (desktop + mobile separately).
   * Thumbnail clicks use `on:click="media-gallery/selectThumbnail"` (see `selectThumbnail`).
   * @param {HTMLElement[]} slidesAll
   * @param {HTMLElement[]} visibleOrder
   */
  #syncThumbnailOrderFromSlideIds(slidesAll, visibleOrder) {
    const visibleIds = new Set(
      visibleOrder.map((slide) => String(slide.getAttribute('slide-id'))).filter(Boolean)
    );

    this.querySelectorAll('slideshow-controls[thumbnails]').forEach((control) => {
      if (!(control instanceof HTMLElement)) return;

      /** @type {Map<string, HTMLElement>} */
      const slideIdToThumbnail = new Map();

      control.querySelectorAll('.slideshow-controls__thumbnail').forEach((thumbnail) => {
        if (!(thumbnail instanceof HTMLElement)) return;

        const slideId = thumbnail.getAttribute('data-slide-id');
        if (slideId) slideIdToThumbnail.set(String(slideId), thumbnail);
      });

      const container = control.querySelector('.slideshow-controls__thumbnails');
      if (!(container instanceof HTMLElement)) return;

      const scrollRoot = control.querySelector('.slideshow-controls__thumbnails-container');
      const prevLeft = scrollRoot instanceof HTMLElement ? scrollRoot.scrollLeft : 0;
      const prevTop = scrollRoot instanceof HTMLElement ? scrollRoot.scrollTop : 0;

      visibleOrder.forEach((slide) => {
        if (!(slide instanceof HTMLElement)) return;

        const slideId = slide.getAttribute('slide-id');
        if (!slideId) return;

        const thumbnail = slideIdToThumbnail.get(String(slideId));
        if (!(thumbnail instanceof HTMLElement)) return;

        const absoluteIndex = slidesAll.indexOf(slide);
        if (absoluteIndex < 0) return;

        thumbnail.setAttribute('data-slide-id', String(slideId));
        thumbnail.setAttribute('data-slide-index', String(absoluteIndex));
        thumbnail.setAttribute('on:click', 'media-gallery/selectThumbnail');
        thumbnail.removeAttribute('hidden');
        thumbnail.removeAttribute('aria-hidden');
        thumbnail.style.display = '';
        container.appendChild(thumbnail);
      });

      slideIdToThumbnail.forEach((thumbnail, slideId) => {
        if (visibleIds.has(slideId)) return;

        thumbnail.setAttribute('hidden', '');
        thumbnail.setAttribute('aria-hidden', 'true');
        thumbnail.style.display = 'none';
        thumbnail.setAttribute('aria-selected', 'false');
      });

      if (scrollRoot instanceof HTMLElement) {
        scrollRoot.scrollLeft = prevLeft;
        scrollRoot.scrollTop = prevTop;
      }
    });
  }

  /**
   * Keep zoom dialog media + thumbnails aligned with the shade-filtered PDP gallery.
   * Absolute indexes stay stable so slide `open/N` handlers keep working.
   * @param {HTMLElement[]} visibleOrder
   */
  #syncZoomDialogFromVisibleOrder(visibleOrder) {
    const zoomDialog = this.refs.zoomDialogComponent;
    if (!zoomDialog) return;

    const visibleIds = new Set(
      visibleOrder.map((slide) => String(slide.getAttribute('slide-id'))).filter(Boolean)
    );
    const includeCustomVideo = visibleOrder.some(
      (slide) => slide instanceof HTMLElement && this.#isCustomMetafieldVideoSlide(slide)
    );

    const setHidden = (el, isHidden) => {
      if (!(el instanceof HTMLElement)) return;
      if (isHidden) {
        el.setAttribute('hidden', '');
        el.setAttribute('aria-hidden', 'true');
        el.style.display = 'none';
      } else {
        el.removeAttribute('hidden');
        el.removeAttribute('aria-hidden');
        el.style.removeProperty('display');
      }
    };

    const zoomMediaItems = zoomDialog.refs?.media;
    if (Array.isArray(zoomMediaItems)) {
      for (const item of zoomMediaItems) {
        if (!(item instanceof HTMLElement)) continue;

        if (item.hasAttribute('data-custom-metafield-video')) {
          setHidden(item, !includeCustomVideo);
          continue;
        }

        const mediaId = item.getAttribute('data-media-id');
        const isVisible = mediaId != null && visibleIds.has(String(mediaId));
        setHidden(item, !isVisible);
      }
    }

    const thumbnailsRoot = zoomDialog.refs?.thumbnails;
    if (!(thumbnailsRoot instanceof HTMLElement)) return;

    thumbnailsRoot.querySelectorAll('.dialog-thumbnails-list__thumbnail').forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      if (thumbnail.hasAttribute('data-custom-metafield-video')) {
        setHidden(thumbnail, !includeCustomVideo);
        if (!includeCustomVideo) thumbnail.setAttribute('aria-selected', 'false');
        return;
      }

      const mediaId = thumbnail.getAttribute('data-media-id');
      const isVisible = mediaId != null && visibleIds.has(String(mediaId));
      setHidden(thumbnail, !isVisible);
      if (!isVisible) thumbnail.setAttribute('aria-selected', 'false');
    });
  }

  /**
   * @param {HTMLElement[]} slides
   */
  #syncLinkedMediaVisibility(slides) {
    this.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnail').forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;

      const slideIndex = Number.parseInt(thumbnail.getAttribute('data-slide-index') ?? '', 10);
      if (Number.isNaN(slideIndex)) return;

      const slide = slides[slideIndex];
      const isHidden = slide?.hasAttribute('hidden') || thumbnail.hasAttribute('hidden');

      if (isHidden) {
        thumbnail.setAttribute('hidden', '');
        thumbnail.setAttribute('aria-hidden', 'true');
        thumbnail.style.display = 'none';
      }
    });

    this.#fixPdpThumbnailControls();
  }

  /**
   * @param {HTMLElement[]} slides
   * @param {HTMLElement | undefined} activeSlide
   */
  #syncThumbnailSelection(slides, activeSlide) {
    if (!activeSlide) return;

    const activeSlideId = activeSlide.getAttribute('slide-id');
    if (!activeSlideId) return;

    this.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnail').forEach((thumbnail) => {
      if (!(thumbnail instanceof HTMLElement)) return;
      if (thumbnail.hasAttribute('hidden')) return;

      const thumbSlideId = thumbnail.getAttribute('data-slide-id');
      const selected = thumbSlideId === activeSlideId;

      thumbnail.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
  }

  #fixPdpThumbnailControls() {
    const fix = () => {
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const slideshow = this.querySelector('slideshow-component') || this.slideshow;
      const slidesEl =
        slideshow instanceof HTMLElement
          ? slideshow.querySelector('slideshow-slides')
          : null;

      for (const control of this.querySelectorAll('slideshow-controls[thumbnails]')) {
        if (!(control instanceof HTMLElement)) continue;

        const isDesktopStrip = control.classList.contains('product-media-gallery__thumbnails');
        const isMobileStrip = control.classList.contains('media-gallery__mobile-controls');
        const position = control.getAttribute('pagination-position');
        const isSideStrip = position === 'left' || position === 'right';

        control.hidden = false;
        control.removeAttribute('hidden');
        control.style.visibility = 'visible';
        control.style.opacity = '1';

        if (isDesktopStrip && isSideStrip && isDesktop) {
          control.style.setProperty('display', 'flex', 'important');
          control.style.setProperty('flex-direction', 'column', 'important');
          control.style.setProperty('align-self', 'start', 'important');
          control.style.setProperty('overflow', 'hidden', 'important');

          // Size from the main image width × aspect ratio so the strip can't
          // inflate the grid (which previously made overflow impossible).
          let slidesHeight = 0;
          const img =
            slidesEl instanceof HTMLElement
              ? slidesEl.querySelector(
                  'slideshow-slide[aria-hidden="false"] img, slideshow-slide:not([hidden]) img, img'
                )
              : null;

          // The ratio comes from the same custom properties base.css sizes the
          // slide with, rather than from the image's intrinsic size. A variant
          // change replaces this element wholesale, so on a shade switch the new
          // image has no naturalWidth yet and each later pass would otherwise
          // measure a different height as it decodes.
          const width = img instanceof HTMLImageElement ? img.clientWidth : 0;
          const ratio =
            readRatio(this, '--gallery-aspect-ratio') ||
            readRatio(img?.closest('.product-media-container'), '--media-preview-ratio') ||
            readRatio(img?.closest('.product-media'), '--ratio');

          if (width > 0 && ratio > 0) {
            slidesHeight = Math.round(width / ratio);
          } else if (img instanceof HTMLImageElement && width > 0) {
            const nw = img.naturalWidth || img.clientWidth;
            const nh = img.naturalHeight || img.clientHeight;
            if (nw > 0 && nh > 0) {
              slidesHeight = Math.round(width * (nh / nw));
            }
          }

          if (slidesHeight < 40 && slidesEl instanceof HTMLElement) {
            // Last resort: temporarily collapse strip and measure slides track
            control.style.setProperty('height', '0px', 'important');
            control.style.setProperty('min-height', '0', 'important');
            void control.offsetHeight;
            slidesHeight = Math.round(slidesEl.getBoundingClientRect().height);
          }

          if (slidesHeight > 40) {
            control.style.setProperty('height', `${slidesHeight}px`, 'important');
            control.style.setProperty('max-height', `${slidesHeight}px`, 'important');
            control.style.setProperty('min-height', '0', 'important');
          } else {
            control.style.setProperty('height', '0', 'important');
            control.style.setProperty('min-height', '100%', 'important');
            control.style.setProperty('max-height', '100%', 'important');
          }
        }

        const container = control.querySelector('.slideshow-controls__thumbnails-container');
        if (!(container instanceof HTMLElement)) continue;

        if (isDesktopStrip && isSideStrip && isDesktop) {
          container.style.setProperty('display', 'block', 'important');
          container.style.setProperty('position', 'relative', 'important');
          container.style.setProperty('flex', '1 1 auto', 'important');
          container.style.setProperty('min-height', '0', 'important');
          container.style.setProperty('height', '100%', 'important');
          container.style.setProperty('max-height', '100%', 'important');
          container.style.setProperty('overflow-x', 'hidden', 'important');
          container.style.setProperty('overflow-y', 'scroll', 'important');
          container.style.setProperty('-webkit-overflow-scrolling', 'touch');
          container.style.setProperty('overscroll-behavior', 'contain');
          container.style.setProperty('cursor', 'grab');
          container.style.setProperty('touch-action', 'none');
          this.#enableThumbnailDragScroll(container, 'y');
        } else if (isMobileStrip || (!isDesktop && isDesktopStrip)) {
          container.style.setProperty('display', 'block', 'important');
          container.style.setProperty('overflow-x', 'auto', 'important');
          container.style.setProperty('overflow-y', 'hidden', 'important');
          container.style.setProperty('-webkit-overflow-scrolling', 'touch');
          container.style.setProperty('overscroll-behavior-x', 'contain');
          container.style.setProperty('overflow-anchor', 'none');
          container.style.setProperty('touch-action', 'pan-x');
          // No custom drag-scroll on mobile — it fights taps on the video thumb.
          // Native overflow scrolling is enough for the horizontal strip.
        }
      }
    };

    fix();
    requestAnimationFrame(fix);
    window.setTimeout(fix, 100);
    window.setTimeout(fix, 500);
    window.setTimeout(fix, 1200);

    const slideshow = this.querySelector('slideshow-component') || this.slideshow;
    if (slideshow instanceof HTMLElement && !this.#thumbnailResizeObserver) {
      this.#thumbnailResizeObserver = new ResizeObserver(() => {
        // Debounce to avoid feedback loops while we set heights
        window.clearTimeout(this.#thumbnailFixTimer);
        this.#thumbnailFixTimer = window.setTimeout(fix, 50);
      });
      this.#thumbnailResizeObserver.observe(slideshow);
      const slidesEl = slideshow.querySelector('slideshow-slides');
      if (slidesEl instanceof HTMLElement) {
        this.#thumbnailResizeObserver.observe(slidesEl);
      }
    }
  }

  /**
   * Enables click-and-drag scrolling on the PDP thumbnail strip.
   * Mouse only — touch/pen use native overflow scrolling so taps (e.g. video
   * thumbnail) select the slide instead of swiping the thumbnails list.
   * @param {HTMLElement} container
   * @param {'x' | 'y'} axis
   */
  #enableThumbnailDragScroll(container, axis) {
    if (container.dataset.dragScrollBound === 'true') return;
    container.dataset.dragScrollBound = 'true';
    container.classList.add('slideshow-controls__thumbnails-container--draggable');

    /** @type {number | null} */
    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;
    let dragging = false;
    let moved = false;
    const dragThresholdPx = 8;

    const onPointerMove = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;

      const dx = event.clientX - startX;
      const dy = event.clientY - startY;

      if (!moved && Math.hypot(dx, dy) > dragThresholdPx) {
        moved = true;
        container.setAttribute('data-dragged', '');
        container.classList.add('is-dragging');

        // Capture only after a real drag so taps still fire click.
        try {
          container.setPointerCapture(event.pointerId);
        } catch {
          // ignore
        }
      }

      if (!moved) return;

      event.preventDefault();

      if (axis === 'y') {
        container.scrollTop = startScrollTop - dy;
      } else {
        container.scrollLeft = startScrollLeft - dx;
      }
    };

    const endDrag = (event) => {
      if (!dragging) return;
      if (event && pointerId != null && event.pointerId !== pointerId) return;

      dragging = false;
      const activePointerId = pointerId;
      pointerId = null;
      container.classList.remove('is-dragging');

      if (activePointerId != null && typeof container.hasPointerCapture === 'function') {
        try {
          if (container.hasPointerCapture(activePointerId)) {
            container.releasePointerCapture(activePointerId);
          }
        } catch {
          // ignore
        }
      }

      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', endDrag);
      document.removeEventListener('pointercancel', endDrag);

      // Clear drag flag after the click event has had a chance to run.
      if (moved) {
        window.setTimeout(() => {
          container.removeAttribute('data-dragged');
        }, 0);
      }
    };

    container.addEventListener('pointerdown', (event) => {
      if (event.button != null && event.button !== 0) return;
      // Touch/pen: native pan-x / overflow scroll. Custom drag is mouse-only.
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (event.target instanceof Element && event.target.closest('a[href]')) return;

      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startScrollLeft = container.scrollLeft;
      startScrollTop = container.scrollTop;
      dragging = true;
      moved = false;
      container.removeAttribute('data-dragged');

      document.addEventListener('pointermove', onPointerMove, { passive: false });
      document.addEventListener('pointerup', endDrag);
      document.addEventListener('pointercancel', endDrag);
    });

    container.addEventListener('dragstart', (event) => {
      event.preventDefault();
    });

    container.addEventListener(
      'click',
      (event) => {
        if (!container.hasAttribute('data-dragged')) return;
        event.preventDefault();
        event.stopPropagation();
        container.removeAttribute('data-dragged');
      },
      true
    );

    // Wheel support when hovering the strip
    container.addEventListener(
      'wheel',
      (event) => {
        if (axis === 'y') {
          if (container.scrollHeight <= container.clientHeight) return;
          event.preventDefault();
          container.scrollTop += event.deltaY;
        } else {
          if (container.scrollWidth <= container.clientWidth) return;
          const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
          event.preventDefault();
          container.scrollLeft += delta;
        }
      },
      { passive: false }
    );
  }

  /**
   * Move Klaviyo's favorites button from product details onto the gallery image.
   */
  #setupKlaviyoFavoritesPlacement() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#syncKlaviyoFavoritesButton();
    window.setTimeout(() => this.#syncKlaviyoFavoritesButton(), 500);
    window.setTimeout(() => this.#syncKlaviyoFavoritesButton(), 2000);

    this.#klaviyoFavoritesObserver?.disconnect();
    this.#klaviyoFavoritesObserver = new MutationObserver(this.#scheduleKlaviyoFavoritesSync);
    this.#klaviyoFavoritesObserver.observe(section, { childList: true, subtree: true });
  }

  /**
   * The observer covers the whole section subtree, so it fires for every gallery
   * swap and every app re-render — and the sync's own appendChild feeds back into
   * it. Collapse each burst into a single pass on the next frame.
   */
  #scheduleKlaviyoFavoritesSync = () => {
    if (this.#klaviyoFavoritesFrame !== null) return;

    this.#klaviyoFavoritesFrame = requestAnimationFrame(() => {
      this.#klaviyoFavoritesFrame = null;
      this.#syncKlaviyoFavoritesButton();
    });
  };

  #syncKlaviyoFavoritesButton() {
    const slot = this.querySelector('[data-pdp-favorites-slot]');
    if (!slot) return;

    let button = slot.querySelector('klaviyo-favorites-button');

    if (!button) {
      for (const candidate of document.querySelectorAll('klaviyo-favorites-button')) {
        if (!this.contains(candidate)) {
          button = candidate;
          break;
        }
      }
    }

    if (!(button instanceof HTMLElement)) return;
    if (button.parentElement === slot) return;

    const source = button.parentElement;
    if (source instanceof HTMLElement) {
      source.classList.add('klaviyo-favorites-button--relocated');
    }

    slot.appendChild(button);
  }
}

/**
 * Reads an aspect ratio from a CSS custom property, accepting both `1.5` and
 * `3 / 2` forms.
 *
 * @param {Element | null | undefined} element
 * @param {string} property
 * @returns {number} The width/height ratio, or 0 when it can't be resolved.
 */
function readRatio(element, property) {
  if (!(element instanceof HTMLElement)) return 0;

  const raw = getComputedStyle(element).getPropertyValue(property).trim();
  if (!raw) return 0;

  const parts = raw.split('/');
  const width = parseFloat(parts[0] ?? '');
  const height = parts.length > 1 ? parseFloat(parts[1] ?? '') : 1;

  if (!(width > 0) || !(height > 0)) return 0;

  return width / height;
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
